/**
 * The exact set of paths that have a localised route under `app/[slug]/`.
 *
 * This exists because the obvious heuristic is wrong. A nav/footer link carries a
 * `messageKey` when its LABEL is translated, and it is tempting to reuse that as
 * "this destination is localised" — but several translated labels point at
 * English-only hubs (`/blog`, `/podcast`, `/tools`, `/alternatives`). Prefixing
 * those would send a locale visitor to a 404.
 *
 * So the set is explicit, and `localized-routes.test.ts` asserts it matches the
 * directories actually present under `src/app/[slug]/`. Add a route without
 * adding it here (or vice versa) and that test fails.
 */
// SAFETY: The surrounding typed contract establishes the asserted representation.
import { DEFAULT_LOCALE, SUPPORTED_LOCALES } from './locales';

// SAFETY: The surrounding typed contract establishes the asserted representation.
export const LOCALIZED_ROUTE_PATHS: readonly string[] = [
  '/', // the locale root, e.g. /es
  '/about',
  '/changelog',
  '/contact',
  '/contact-sales',
  '/cookie-policy',
  '/creators',
  '/data-protection',
  '/demo',
  '/enterprise',
  '/faq',
  '/features',
  '/pricing',
  '/privacy',
  '/sales',
  '/stats',
  '/status',
  '/teams',
  '/terms',
  '/use-cases',
] as const;

const LOCALIZED = new Set(LOCALIZED_ROUTE_PATHS);

/** True when `path` (no locale prefix, no query) has a localised counterpart. */
export function hasLocalizedRoute(path: string): boolean {
  return LOCALIZED.has(path);
}

/**
 * A dead locale-prefixed URL and where it should 301 to, or `null` to leave it alone.
 *
 * Background: `src/app/[slug]/` mirrors exactly the 20 paths in LOCALIZED_ROUTE_PATHS
 * under a locale segment. Everything else the site serves — the pSEO families
 * (`/speech-to-text/*`, `/voice/*`, `/tools/*`, `/alternatives/*`, `/podcast/*`,
 * `/blog/*`, and the `/use-cases/<detail>` children) — exists ONLY unprefixed. A
 * locale-prefixed request for any of those has always 404'd.
 *
 * Those 404s stopped being emitted on 2026-08-15 (94bc0ec) and no locale URL appears
 * in the sitemap, but GSC still listed 148 of them on 2026-09-04 with last-crawl dates
 * of Aug 20-28 — Google re-crawling URLs it already knows. A 301 to the English page
 * ends that drain and forwards whatever link equity those URLs picked up.
 *
 * The match is EXACT on the post-locale path, not a prefix. `/ja/use-cases` is a real
 * localised hub (200) while `/ja/use-cases/aac-transcription` is not (404), so a prefix
 * rule would wrongly redirect the hub. Verified live 2026-09-04 against both sets.
 *
 * Not a locale prefix, or a path that genuinely has a localised route → `null`, and the
 * request continues untouched. `en` is never prefixed in the first place.
 */
export function localeRedirectTarget(pathname: string): string | null {
  const segments = pathname.split('/').filter(Boolean);

  if (segments.length < 2) return null; // '/' or a bare '/es' locale root — both real.

  const [maybeLocale, ...rest] = segments;

  if (maybeLocale === DEFAULT_LOCALE) return null;

  if (!(/* SAFETY: The surrounding typed contract establishes the asserted representation. */ SUPPORTED_LOCALES as readonly string[]).includes(maybeLocale)) return null;

  const remainder = `/${rest.join('/')}`;

  if (hasLocalizedRoute(remainder)) return null; // e.g. /es/sales, /ja/use-cases

  return remainder;
}
